import { Observable } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { LifecycleHooks } from '@app/core/services/lifecycle-hooks.service';
import { shareRef } from '../../operators/share-ref.operator';
import { StepWizardStepDirective } from './directives/step-wizard-step.directive';
import { StepWizardTitleDirective } from './directives/step-wizard-title.directive';
import { StepWizardService } from './step-wizard.service';

@Component({
  standalone: false,
  selector: 'adspt-step-wizard',
  templateUrl: 'step-wizard.component.html',
  styleUrls: ['step-wizard.component.scss'],
  providers: [StepWizardService, LifecycleHooks],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('parent', [transition(':enter', [])]),
    trigger('widthAnimate', [
      transition(':enter', [
        style({ width: 0, opacity: 0 }),
        animate('200ms ease-in-out', style({ width: '*' })),
        animate('100ms ease-in-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('100ms ease-in-out', style({ opacity: 0 })),
        animate('200ms ease-in-out', style({ width: 0 })),
      ]),
    ]),
  ],
})
export class StepWizardComponent implements OnInit {
  @ContentChild(StepWizardTitleDirective, { static: true })
  title: StepWizardTitleDirective;

  @ContentChildren(StepWizardStepDirective, { emitDistinctChangesOnly: true })
  set steps(steps: QueryList<StepWizardStepDirective>) {
    this.sws.setStepsQuery(steps);
  }

  @Input()
  heapPrefix?: string;

  @Input()
  set activeIdx(idx: number) {
    this.sws.setActiveIdx(idx);
  }

  @Output()
  readonly close: Observable<boolean> = this.sws.close$;

  readonly stepSideTmpl$: Observable<TemplateRef<any> | number> = this.sws.activeStep$.pipe(
    switchMap((step) => step.sideStep$),
    map((sideStep) => sideStep || 0),
    debounceTime(1),
    shareRef(),
  );

  constructor(
    readonly sws: StepWizardService,
    private router: Router,
    private lh: LifecycleHooks,
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.lh.destroy$),
      )
      .subscribe(() => this.sws.close$.next(false));
  }

  stepForward(): void {
    this.sws.stepForward();
  }

  saveAndClose(): void {
    this.sws.saveAndClose();
  }
}
